import React, { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDay, hasAnyRole } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Search, Plus, AlertTriangle, Clock, Pencil, Trash2, Download, ClipboardCheck,
  Package, Snowflake, Leaf, Beef, Fish, Box, Wine, Cookie, Utensils, Coffee, Pizza, CakeSlice, Save,
} from "lucide-react";
import { toast } from "sonner";
import { downloadReport } from "@/lib/pdf";

// Lucide icon palette for inventory category icons
const CAT_ICONS_MAP = { Package, Snowflake, Leaf, Beef, Fish, Box, Wine, Cookie, Utensils, Coffee, Pizza, CakeSlice };
const CAT_ICON_KEYS = Object.keys(CAT_ICONS_MAP);
function CatIcon({ name, className = "w-3.5 h-3.5" }) {
  const Cmp = CAT_ICONS_MAP[name] || Package;
  return <Cmp className={className} />;
}

export default function InventoryPage() {
  const { user } = useAuth();
  const canEdit = hasAnyRole(user, ["admin", "operations"]);
  const isAdmin = hasAnyRole(user, ["admin"]);
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState({ low_stock: [], expiring: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [movItem, setMovItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [stocktakeItem, setStocktakeItem] = useState(null);

  const load = async () => {
    const [i, a, s, c] = await Promise.all([
      api.get("/inventory"),
      api.get("/inventory/alerts"),
      api.get("/suppliers").catch(() => ({ data: [] })),
      api.get("/inventory/categories").catch(() => ({ data: [] })),
    ]);
    setItems(i.data); setAlerts(a.data); setSuppliers(s.data || []); setCategories(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const catMap = Object.fromEntries(categories.map((c) => [c.key, c]));
  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || null;

  const del = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/inventory/${it.id}`);
      toast.success("Item deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const exportPDF = () => {
    downloadReport({
      title: "Inventory Report",
      filename: `Inventory-${new Date().toISOString().slice(0, 10)}.pdf`,
      kpis: [
        { label: "Total SKUs", value: items.length },
        { label: "Low Stock", value: alerts.low_stock?.length || 0 },
        { label: "Expiring (7d)", value: alerts.expiring?.length || 0 },
        { label: "Total Value", value: fmtCurrency(items.reduce((s, i) => s + (i.qty || 0) * (i.price_per_unit || 0), 0)) },
      ],
      sections: [
        {
          title: "Full Stock List",
          columns: ["Item", "Category", "Qty", "Unit", "Reorder", "Price/Unit", "Total Value"],
          rows: items.map((i) => [
            i.name, i.category, String(i.qty), i.unit, String(i.reorder_level),
            fmtCurrency(i.price_per_unit), fmtCurrency((i.qty || 0) * (i.price_per_unit || 0)),
          ]),
          columnStyles: { 2: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
        },
        ...(alerts.low_stock?.length ? [{
          title: `Low Stock Alerts (${alerts.low_stock.length})`,
          columns: ["Item", "Current Qty", "Reorder Level"],
          rows: alerts.low_stock.map((i) => [i.name, `${i.qty} ${i.unit}`, `${i.reorder_level} ${i.unit}`]),
        }] : []),
      ],
    });
  };

  const filtered = items.filter((it) => {
    if (cat && it.category !== cat) return false;
    if (q && !it.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="inventory-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Stock</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Inventory</h1>
        </div>
        <div className="flex gap-2">
        <Button onClick={() => setShowForm(true)} data-testid="new-inv-item" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Add Item
        </Button>
        <Button onClick={exportPDF} data-testid="inv-pdf" variant="outline" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm">
          <Download className="w-4 h-4 mr-1.5" /> PDF
        </Button>
        </div>
      </div>

      {/* Alerts */}
      {(alerts.low_stock.length > 0 || alerts.expiring.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {alerts.low_stock.length > 0 && (
            <div className="card-lux p-4 border-amber-500/30">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-400" /><div className="label-eyebrow text-amber-400">Low stock · {alerts.low_stock.length}</div></div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {alerts.low_stock.slice(0, 5).map((i) => (
                  <div key={i.id} className="text-xs flex justify-between"><span className="text-zinc-300">{i.name}</span><span className="text-amber-400">{i.qty} {i.unit}</span></div>
                ))}
              </div>
            </div>
          )}
          {alerts.expiring.length > 0 && (
            <div className="card-lux p-4 border-red-500/30">
              <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-red-400" /><div className="label-eyebrow text-red-400">Expiring soon · {alerts.expiring.length}</div></div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {alerts.expiring.slice(0, 5).map((i) => (
                  <div key={i.id} className="text-xs flex justify-between"><span className="text-zinc-300">{i.name}</span><span className="text-red-400">{fmtDay(i.expiry_date)}</span></div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input placeholder="Search items…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search" className="pl-9 bg-[#0a0a0a] border-[#2a2a2a] h-11 rounded-sm" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="cat-filter" className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm px-3 h-11 text-sm">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.key} value={c.key}>{c.name_en}</option>)}
        </select>
      </div>

      {/* Items grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((it) => {
          const meta = catMap[it.category];
          const color = meta?.color || "#D4AF37";
          const low = it.qty <= it.reorder_level;
          return (
            <div key={it.id} className="card-lux p-4 flex items-start gap-3 relative" data-testid={`inv-${it.id}`}>
              <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${color}15`, color }}>
                <CatIcon name={meta?.icon} className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium truncate">{it.name}</div>
                  <div className="flex items-center gap-1">
                    {low && <span className="text-[10px] text-amber-400">LOW</span>}
                    {canEdit && (
                      <button onClick={() => setStocktakeItem(it)} data-testid={`inv-stocktake-${it.id}`} title="Stocktake"
                        className="p-1 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-emerald-400 transition-colors">
                        <ClipboardCheck className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canEdit && (
                      <button onClick={() => setEditItem(it)} data-testid={`inv-edit-${it.id}`} title="Edit"
                        className="p-1 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-[#D4AF37] transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => del(it)} data-testid={`inv-del-${it.id}`} title="Delete"
                        className="p-1 rounded-sm hover:bg-[#1a1a1a] text-zinc-400 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-600 font-mono">{it.sku || "—"}</div>
                <div className="text-xs mt-0.5" style={{ color: `${color}CC` }}>{meta?.name_en || it.category}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="font-display text-lg" style={{ color: low ? "#F59E0B" : "#FAFAFA" }}>{it.qty} <span className="text-xs text-zinc-500">{it.unit}</span></div>
                  <button onClick={() => setMovItem(it)} className="text-xs text-[#D4AF37] hover:text-[#E5C86B]">Adjust</button>
                </div>
                <div className="text-[10px] text-zinc-600 mt-1">{fmtCurrency(it.price_per_unit)}/{it.unit} · reorder @{it.reorder_level}</div>
                {supplierName(it.supplier_id) && (
                  <div className="text-[10px] text-[#D4AF37]/80 mt-1 truncate" title={supplierName(it.supplier_id)} data-testid={`inv-supplier-${it.id}`}>
                    <span className="text-zinc-600">Supplier:</span> {supplierName(it.supplier_id)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <NewItemForm open={showForm} editing={editItem} suppliers={suppliers} categories={categories} onCategoriesChanged={load} onClose={() => { setShowForm(false); setEditItem(null); }} onSaved={() => { setShowForm(false); setEditItem(null); load(); toast.success(editItem ? "Item updated" : "Item added"); }} />
      <MovementForm item={movItem} onClose={() => setMovItem(null)} onSaved={() => { setMovItem(null); load(); toast.success("Stock updated"); }} />
      <StocktakeDialog item={stocktakeItem} onClose={() => setStocktakeItem(null)} onSaved={() => { setStocktakeItem(null); load(); toast.success("Stock balance updated"); }} />
    </div>
  );
}

function NewItemForm({ open, editing, suppliers = [], categories = [], onCategoriesChanged, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", sku: "", category: "dry", unit: "kg",
    qty: 0, reorder_level: 5, price_per_unit: 0,
    primary_supplier_id: "", secondary_supplier_id: "",
    moq: "", lead_time_days: "", last_purchase_price: "",
    location: "warehouse", expiry_date: "", notes: "",
  });
  const [catModal, setCatModal] = useState(null); // null | {} (new) | {existing} (edit)

  useEffect(() => {
    if (editing) {
      setF({
        name: editing.name || "",
        sku: editing.sku || "",
        category: editing.category || (categories[0]?.key || "dry"),
        unit: editing.unit || "kg",
        qty: editing.qty || 0,
        reorder_level: editing.reorder_level || 5,
        price_per_unit: editing.price_per_unit || 0,
        primary_supplier_id: editing.primary_supplier_id || editing.supplier_id || "",
        secondary_supplier_id: editing.secondary_supplier_id || "",
        moq: editing.moq ?? "",
        lead_time_days: editing.lead_time_days ?? "",
        last_purchase_price: editing.last_purchase_price ?? "",
        location: editing.location || "warehouse",
        expiry_date: editing.expiry_date ? editing.expiry_date.slice(0, 10) : "",
        notes: editing.notes || "",
      });
    } else if (open) {
      setF({
        name: "", sku: "", category: categories[0]?.key || "dry", unit: "kg",
        qty: 0, reorder_level: 5, price_per_unit: 0,
        primary_supplier_id: "", secondary_supplier_id: "",
        moq: "", lead_time_days: "", last_purchase_price: "",
        location: "warehouse", expiry_date: "", notes: "",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, open]);

  const onCategorySelectChange = (val) => {
    if (val === "__new__") { setCatModal({}); return; }
    setF({ ...f, category: val });
  };

  const saveCategory = async (c) => {
    try {
      const body = { name_en: c.name_en, name_cn: c.name_cn, color: c.color, icon: c.icon };
      if (c.id) {
        await api.patch(`/inventory/categories/${c.id}`, body);
        toast.success("Category updated");
      } else {
        const r = await api.post("/inventory/categories", body);
        toast.success("Category created");
        if (r.data?.key) setF((prev) => ({ ...prev, category: r.data.key }));
      }
      setCatModal(null);
      onCategoriesChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name_en}"? Built-in categories can't be deleted, and a category in use must be reassigned first.`)) return;
    try { await api.delete(`/inventory/categories/${c.id}`); toast.success("Category deleted"); setCatModal(null); onCategoriesChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const currentCat = categories.find((c) => c.key === f.category);

  const save = async () => {
    try {
      const payload = {
        ...f,
        sku: f.sku || null,
        primary_supplier_id: f.primary_supplier_id || null,
        secondary_supplier_id: f.secondary_supplier_id || null,
        supplier_id: f.primary_supplier_id || null,
        moq: f.moq === "" ? null : Number(f.moq),
        lead_time_days: f.lead_time_days === "" ? null : Number(f.lead_time_days),
        last_purchase_price: f.last_purchase_price === "" ? null : Number(f.last_purchase_price),
        expiry_date: f.expiry_date || null,
      };
      if (editing) {
        await api.patch(`/inventory/${editing.id}`, payload);
      } else {
        await api.post("/inventory", payload);
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <Dialog open={!!(open || editing)} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit Inventory Item" : "Add Inventory Item"}</DialogTitle>
          <DialogDescription className="sr-only">{editing ? "Update the details of this inventory item." : "Provide details for a new inventory item."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Identification */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Item Name">
                <Input placeholder="e.g. Basmati Rice" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="inv-name" className="bg-[#050505] border-[#2a2a2a]" />
              </Field>
            </div>
            <Field label="SKU (auto if blank)">
              <Input placeholder="e.g. INV-DRY-001" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} data-testid="inv-sku" className="bg-[#050505] border-[#2a2a2a] font-mono text-xs" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <div className="flex items-stretch gap-1">
                <select value={f.category} onChange={(e) => onCategorySelectChange(e.target.value)} data-testid="inv-category" className="flex-1 bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                  {categories.map((c) => <option key={c.key} value={c.key}>{c.name_en}{c.name_cn ? ` · ${c.name_cn}` : ""}</option>)}
                  {f.category && !categories.find((c) => c.key === f.category) && (
                    <option value={f.category}>{f.category}</option>
                  )}
                  <option value="__new__">＋ New category…</option>
                </select>
                {currentCat && (
                  <Button type="button" onClick={() => setCatModal(currentCat)} variant="outline" data-testid="inv-category-edit" className="h-10 w-10 p-0 border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm" title="Edit this category">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Unit of Measure">
              <Input placeholder="kg, pcs, box…" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="inv-unit" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
            <Field label="Storage Location">
              <Input placeholder="warehouse / freezer-1 / dry-store" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="inv-location" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
            <Field label="Expiry Date (optional)">
              <Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="inv-expiry" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-[#1a1a1a]">
            <Field label="Current Stock">
              <Input type="number" placeholder="0" value={f.qty} onChange={(e) => setF({ ...f, qty: Number(e.target.value) })} data-testid="inv-qty" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
            <Field label="Par Stock (reorder)">
              <Input type="number" placeholder="5" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: Number(e.target.value) })} data-testid="inv-reorder" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
            <Field label="Cost Price (RM)">
              <Input type="number" step="0.01" placeholder="0.00" value={f.price_per_unit} onChange={(e) => setF({ ...f, price_per_unit: Number(e.target.value) })} data-testid="inv-price" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#1a1a1a]">
            <Field label="Primary Supplier">
              <select value={f.primary_supplier_id} onChange={(e) => setF({ ...f, primary_supplier_id: e.target.value })} data-testid="inv-primary-supplier" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                <option value="">— None —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Secondary Supplier (backup)">
              <select value={f.secondary_supplier_id} onChange={(e) => setF({ ...f, secondary_supplier_id: e.target.value })} data-testid="inv-secondary-supplier" className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
                <option value="">— None —</option>
                {suppliers.filter((s) => s.id !== f.primary_supplier_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="MOQ (min order qty)">
              <Input type="number" placeholder="leave blank to use supplier MOQ" value={f.moq} onChange={(e) => setF({ ...f, moq: e.target.value })} data-testid="inv-moq" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
            <Field label="Lead Time (days)">
              <Input type="number" placeholder="leave blank to use supplier lead time" value={f.lead_time_days} onChange={(e) => setF({ ...f, lead_time_days: e.target.value })} data-testid="inv-lead" className="bg-[#050505] border-[#2a2a2a]" />
            </Field>
            <div className="col-span-2">
              <Field label="Last Purchase Price (RM)">
                <Input type="number" step="0.01" placeholder="auto-updated when PO is delivered" value={f.last_purchase_price} onChange={(e) => setF({ ...f, last_purchase_price: e.target.value })} data-testid="inv-last-price" className="bg-[#050505] border-[#2a2a2a]" />
              </Field>
            </div>
          </div>

          <Button onClick={save} data-testid="inv-save" disabled={!f.name} className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">{editing ? "Save Changes" : "Add Item"}</Button>
        </div>
        {catModal && (
          <InvCategoryEditor
            category={catModal}
            onClose={() => setCatModal(null)}
            onSave={saveCategory}
            onDelete={catModal.id ? () => deleteCategory(catModal) : null}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function InvCategoryEditor({ category, onClose, onSave, onDelete }) {
  const [f, setF] = useState({
    id: category.id,
    name_en: category.name_en || "",
    name_cn: category.name_cn || "",
    color: category.color || "#10B981",
    icon: category.icon || "Package",
    is_builtin: !!category.is_builtin,
  });
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-lg" data-testid="inv-category-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{f.id ? "Edit Category" : "New Category"}</DialogTitle>
          <DialogDescription className="sr-only">Add or edit an inventory category. Categories group items in the warehouse view and stock alerts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name · English"><Input value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} placeholder="e.g. Halal Drinks" data-testid="inv-cat-name-en" className="bg-[#050505] border-[#2a2a2a]" /></Field>
            <Field label="Name · 中文"><Input value={f.name_cn} onChange={(e) => setF({ ...f, name_cn: e.target.value })} placeholder="例如: 清真饮料" data-testid="inv-cat-name-cn" className="bg-[#050505] border-[#2a2a2a]" /></Field>
          </div>
          <Field label="Color">
            <div className="flex items-center gap-2">
              <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} data-testid="inv-cat-color" className="w-12 h-10 bg-transparent border border-[#2a2a2a] rounded-sm cursor-pointer" />
              <Input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="bg-[#050505] border-[#2a2a2a] font-mono" />
            </div>
          </Field>
          <Field label="Icon">
            <div className="grid grid-cols-6 gap-2">
              {CAT_ICON_KEYS.map((k) => {
                const Cmp = CAT_ICONS_MAP[k];
                const selected = f.icon === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setF({ ...f, icon: k })}
                    data-testid={`inv-cat-icon-${k}`}
                    title={k}
                    className={`flex items-center justify-center h-10 rounded-sm border transition-colors ${selected ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#3a3a3a]"}`}
                  >
                    <Cmp className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </Field>
          {f.is_builtin && (
            <p className="text-[10px] text-zinc-500">This is a built-in category. You can rename, recolor or change its icon — but it can't be deleted.</p>
          )}
          <div className="flex justify-between items-center gap-2 pt-2">
            <div>
              {onDelete && !f.is_builtin && (
                <Button onClick={onDelete} variant="outline" data-testid="inv-cat-delete" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
              <Button onClick={() => onSave(f)} disabled={!f.name_en} data-testid="inv-cat-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
                <Save className="w-3.5 h-3.5 mr-1.5" />{f.id ? "Save Changes" : "Create Category"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-eyebrow text-[10px]">{label}</Label>
      {children}
    </div>
  );
}

function MovementForm({ item, onClose, onSaved }) {
  const [type, setType] = useState("in");
  const [qty, setQty] = useState(0);
  const [note, setNote] = useState("");
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white">
        <DialogHeader><DialogTitle className="font-display text-2xl">{item?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-500">Current: <span className="text-[#D4AF37]">{item?.qty} {item?.unit}</span></div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm">
            <option value="in">Stock In</option><option value="out">Stock Out</option><option value="waste">Waste</option><option value="damage">Damage</option><option value="transfer">Transfer</option>
          </select>
          <Input type="number" placeholder="Quantity" value={qty} onChange={(e) => setQty(Number(e.target.value))} data-testid="mov-qty" className="bg-[#050505] border-[#2a2a2a]" />
          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#050505] border-[#2a2a2a]" />
          <Button onClick={async () => { await api.post("/inventory/movement", { item_id: item.id, type, qty, note }); onSaved?.(); }} data-testid="mov-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C86B]">Record Movement</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StocktakeDialog({ item, onClose, onSaved }) {
  const [actualQty, setActualQty] = useState(0);
  const [note, setNote] = useState("");
  useEffect(() => { if (item) { setActualQty(item.qty || 0); setNote(""); } }, [item]);
  if (!item) return null;
  const delta = Number(actualQty) - Number(item.qty || 0);
  const submit = async () => {
    try {
      await api.post("/inventory/stocktake", { item_id: item.id, actual_qty: Number(actualQty), note });
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Stocktake failed"); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-md" data-testid="stocktake-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Stocktake</DialogTitle>
          <DialogDescription className="sr-only">Update the physical stock balance for an inventory item.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-sm bg-[#050505] border border-[#1a1a1a] p-3">
            <div className="text-sm font-medium">{item.name}</div>
            <div className="text-xs text-zinc-500 font-mono">{item.sku || "—"} · {item.unit}</div>
            <div className="text-xs text-zinc-500 mt-2">System balance: <span className="text-zinc-300">{item.qty} {item.unit}</span></div>
          </div>
          <Field label="Actual Count (physical)">
            <Input type="number" step="0.01" value={actualQty} onChange={(e) => setActualQty(e.target.value)} data-testid="stocktake-qty" className="bg-[#050505] border-[#2a2a2a]" autoFocus />
          </Field>
          {delta !== 0 && (
            <div className={`text-xs ${delta > 0 ? "text-emerald-400" : "text-amber-400"}`}>
              Adjustment: {delta > 0 ? "+" : ""}{delta.toFixed(2)} {item.unit}
            </div>
          )}
          <Field label="Note (optional)">
            <Input placeholder="e.g. Weekly count, spillage, etc." value={note} onChange={(e) => setNote(e.target.value)} data-testid="stocktake-note" className="bg-[#050505] border-[#2a2a2a]" />
          </Field>
          <Button onClick={submit} data-testid="stocktake-save" className="w-full bg-emerald-500 text-black hover:bg-emerald-400">Confirm Stocktake</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

